import i18n from "@/translations/i18n"

/**
 * Outcome of a perform_action cloud function call on the device.
 *
 * 0 means success; 1/2/3 are legacy generic failure variants;
 * negatives are application-specific failure reasons in reserved
 * ranges (see backend constants.DeviceReturnCode for the full
 * contract).
 *
 * New reasons are *added* as additional negatives within their
 * reserved range — existing values never get renumbered.
 * {@link translateDeviceReturnCode} falls back to a generic
 * "(code N)" message for unknown future codes so a firmware
 * release isn't blocked on a frontend deploy.
 */
export enum DeviceReturnCode {
  // Generic
  SUCCESS = 0,
  FAILURE = 1,
  REFUSAL = 2,
  ERROR = 3,

  // Shared input validation (-10..-19)
  ACTION_JSON_INVALID = -10,
  ACTION_UNKNOWN = -11,

  // VFD / current control (-100..-199)
  VFD_DAC_UNREACHABLE = -100,
  VFD_RAMP_SECONDS_OUT_OF_RANGE = -101,
  VFD_PRESSURE_SENSOR_ID_MISMATCH = -102,
  VFD_FLOW_SENSOR_ID_MISMATCH = -103,
  VFD_PID_TYPE_UNSUPPORTED = -104,
  CURRENT_CONTROL_RAMP_FAILED = -105,

  // Reel (-400..-499)
  REEL_CANNOT_SET_COUNTS_WHILE_EXTENDING = -400,

  // Center Pivot (-500..-599)
  //
  // All three refuse the *entire* CPSET rather than partially
  // applying it, so a combined "start moving at a target speed"
  // message that trips one of these does not start the pivot. They
  // are configuration errors, not transient failures — retrying
  // without fixing the configuration will not help.
  CP_SPEED_PID_NO_FEEDBACK = -500,
  CP_SPEED_COMMAND_CONFLICT = -501,
  CP_SPEED_PID_NOT_CONFIGURED = -502,

  // IO Expander (-600..-699)
  IOEX_NOT_INITIALIZED = -600,
  IOEX_BITS_INVALID = -601,

  // Device (-700..-799)
  DEVICE_SLEEP_NOT_ACTIVE = -700,
}

/**
 * Map a {@link DeviceReturnCode} (or any int — the backend may
 * surface unknown future codes from newer firmware) to a localized
 * message.
 *
 * Shape: ``<call-to-action> (code <NAME>/<int>)``. Every error
 * surfaces a CTA; codes with code-specific guidance (e.g.
 * ``"check your 4-20 mA wiring"``) override the default
 * (``"contact support"``). The trailing ``(code NAME/int)`` gives
 * operators a stable identifier to share with support regardless of
 * locale.
 *
 * Unknowns (including SUCCESS — never displayed as an error in real
 * flows) fall through to ``templateUnknown`` which keeps the
 * default contact-support CTA and shows just the bare int.
 */
export function translateDeviceReturnCode(
  code: DeviceReturnCode | number | null | undefined,
): string {
  if (code === null || code === undefined) {
    return i18n.t("deviceReturnCodes:templateUnknown", { code: "?" })
  }
  // ``DeviceReturnCode[code]`` is a numeric-enum reverse lookup
  // returning the symbolic name for known members and ``undefined``
  // otherwise.
  const name = DeviceReturnCode[code] as string | undefined
  if (!name || name === "SUCCESS") {
    return i18n.t("deviceReturnCodes:templateUnknown", { code })
  }
  const specificKey = `deviceReturnCodes:cta_${name}`
  const cta = i18n.exists(specificKey)
    ? i18n.t(specificKey as never)
    : i18n.t("deviceReturnCodes:cta_default")
  return i18n.t("deviceReturnCodes:template", { name, code, cta })
}
